/*
 * Source: https://www.nsave.com/terms/cookie (fetched live).
 * Modeled on `privacy.ts` — numbered sections with paragraphs, lists and a table.
 * Copy below is reproduced verbatim from the live page.
 */

/** A single ordered block within a section body. */
export type CookieBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; columns: string[]; rows: string[][] };

export interface CookieSection {
  number: string;
  title: string;
  body: CookieBlock[];
}

export const cookieContent = {
  title: 'Cookie Policy',
  lastUpdated: '15 September 2023',
  intro: [
    'nsave Ltd., with registered address at 124 City Road, London, United Kingdom EC1V 2NX (“nsave”, the “Company", "we", "us" or "our") may use cookies and other tracking technologies when you visit our website "www.nsave.com", including any other form of media, media channel, mobile website or mobile application linked or connected to it (collectively, the "Website") to help personalize the Website and enhance your user experience.',
    'If you have any questions or complaints about this Cookie Policy, you can contact us at data@nsave.com with the subject "Cookies".',
  ],
  sections: [
    {
      number: '1',
      title: 'Use of Cookies',
      body: [
        {
          type: 'paragraph',
          text: 'A "cookie" is a set of data (a unique identifier) that a server sends to your browser and is stored on your computer. Your browser then provides this unique identifier to be used each time you submit a request to the Website. We use cookies on the Website to, among other things, keep track of the services you have used, record registration information, store your user preferences, keep you logged in to the Website, facilitate your use of the Website, and track the pages you visit. Cookies help us understand how the Website is used and improve your user experience.',
        },
      ],
    },
    {
      number: '2',
      title: 'Our cookies',
      body: [
        {
          type: 'paragraph',
          text: 'Our cookies are "first party cookies" (set by our domain; as opposed to third-party cookies), and may be permanent or temporary. These are necessary cookies, without which the Website would not function properly or be able to provide certain features and functionalities. Some of them can be manually disabled in your browser, but may affect the functionality of the Site.',
        },
      ],
    },
    {
      number: '3',
      title: 'Used cookies',
      body: [
        {
          type: 'paragraph',
          text: 'When you connect to the Website, a cookie banner appears. Subject to your choices, the following cookies will be stored in your device’s memory.',
        },
        {
          type: 'table',
          columns: ['Name', 'Type', 'Source', 'Expiration'],
          rows: [['Waitlist user identifier', 'Functional', 'First-party', '7 days']],
        },
      ],
    },
    {
      number: '4',
      title: 'Control',
      body: [
        {
          type: 'paragraph',
          text: 'Most browsers are set to accept cookies by default. However, you can delete or reject cookies in your browser settings. Please be aware that doing so may affect the availability and functionality of the Site. You may also reject cookies by selecting the relevant option on our cookie banner. For more information on how to control cookies:',
        },
        {
          type: 'list',
          items: [
            'Apple Safari : https://support.apple.com/fr-fr/guide/safari/sfri11471/mac',
            'Google Chrome: https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DDesktop&hl=en',
            'Microsoft Edge : https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd',
            'Microsoft Internet Explorer: https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d',
            'Mozilla Firefox : https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences&redirectlocale=en-US',
            'Android (Chrome) : https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DAndroid&hl=en&oco',
          ],
        },
      ],
    },
    {
      number: '5',
      title: 'Amendments',
      body: [
        {
          type: 'list',
          items: [
            'We encourage you to review this Cookie Policy regularly to stay informed of any updates. You will be deemed to have read and accepted the changes if you continue to use the Website after the date of posting of the revised Cookie Policy or if you accept their use through our cookie banner.',
            'We reserve the right to make changes to this Cookie Policy at any time and for any reason. We will notify you of any changes by updating the "last updated" date on this Cookie Policy. Any changes or modifications will be effective immediately upon posting the updated Cookie Policy on the Website, and you waive the right to receive specific notice of each such change or modification.',
          ],
        },
      ],
    },
    {
      number: '6',
      title: 'Privacy policy',
      body: [
        {
          type: 'paragraph',
          text: 'For further information on how we process your personal data, please refer to our privacy policy nsave.com/privacy-policy',
        },
      ],
    },
  ] satisfies CookieSection[],
};
